/**
 * Página principal de reportes
 *
 * Muestra las diferentes opciones de reportes disponibles en el sistema.
 *
 * @module pages/reportes/Reportes
 * @requires react, react-router-dom
 * @related_files ./ReporteVentas.js, ./ReporteCompras.js, ./ReporteGanancias.js
 */

import React from 'react';
import { Link } from 'react-router-dom';

// Componentes
import Card from '../../components/common/Card';

// Iconos
import {
  FaChartBar, FaChartLine, FaChartPie, FaShoppingBag,
  FaShoppingCart, FaDollarSign, FaCalendarAlt, FaFileAlt, FaTrophy
} from 'react-icons/fa';

/**
 * Componente de página principal de reportes
 * @returns {JSX.Element} Componente Reportes
 */
const Reportes = () => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-e0e3-foreground">Reportes e Informes</h1>
          <p className="mt-1 text-sm text-e0e3-muted">
            Elegí un módulo para ver el detalle. Los indicadores se consultan dentro de cada reporte.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Tarjeta: Reporte de Ventas */}
        <Link to="/reportes/ventas" className="block transition transform hover:scale-105">
          <Card
            title="Reporte de Ventas"
            icon={<FaShoppingBag />}
          >
            <div className="flex justify-between items-center">
              <div className="space-y-2">
                <p className="text-gray-600">
                  Consulta la evolución y composición general de tus ventas.
                </p>
                <div className="flex space-x-2 text-xs text-gray-500">
                  <span className="flex items-center">
                    <FaCalendarAlt className="mr-1" /> Diario
                  </span>
                  <span className="flex items-center">
                    <FaCalendarAlt className="mr-1" /> Mensual
                  </span>
                  <span className="flex items-center">
                    <FaCalendarAlt className="mr-1" /> Anual
                  </span>
                </div>
              </div>
              <div className="p-3 bg-indigo-100 text-indigo-600 rounded-full">
                <FaChartLine className="h-6 w-6" />
              </div>
            </div>
          </Card>
        </Link>

        {/* Tarjeta: Estadísticas de Ventas */}
        <Link to="/reportes/productos" className="block transition transform hover:scale-105">
          <Card
            title="Estadísticas de Ventas"
            icon={<FaTrophy />}
          >
            <div className="flex justify-between items-center">
              <div className="space-y-2">
                <p className="text-gray-600">
                  Descubrí qué productos venden más, cuánto facturan y cómo participan en tus ventas.
                </p>
                <div className="flex space-x-2 text-xs text-gray-500">
                  <span className="flex items-center">
                    <FaTrophy className="mr-1" /> Ranking
                  </span>
                  <span className="flex items-center">
                    <FaChartBar className="mr-1" /> Productos
                  </span>
                </div>
              </div>
              <div className="p-3 bg-amber-100 text-amber-700 rounded-full">
                <FaTrophy className="h-6 w-6" />
              </div>
            </div>
          </Card>
        </Link>

        {/* Tarjeta: Reporte de Ganancias */}
        <Link to="/reportes/ganancias" className="block transition transform hover:scale-105">
          <Card
            title="Reporte de Ganancias"
            icon={<FaDollarSign />}
          >
            <div className="flex justify-between items-center">
              <div className="space-y-2">
                <p className="text-gray-600">
                  Analizá rentabilidad, costos y ganancias.
                </p>
                <div className="flex space-x-2 text-xs text-gray-500">
                  <span className="flex items-center">
                    <FaCalendarAlt className="mr-1" /> Por Período
                  </span>
                  <span className="flex items-center">
                    <FaChartLine className="mr-1" /> Tendencias
                  </span>
                </div>
              </div>
              <div className="p-3 bg-purple-100 text-purple-600 rounded-full">
                <FaChartPie className="h-6 w-6" />
              </div>
            </div>
          </Card>
        </Link>

        {/* Tarjeta: Reporte de Compras */}
        <Link to="/reportes/compras" className="block transition transform hover:scale-105">
          <Card
            title="Compras"
            icon={<FaShoppingCart />}
          >
            <div className="flex justify-between items-center">
              <div className="space-y-2">
                <p className="text-gray-600">
                  Registro de compras a proveedores, gastos e inventario adquirido.
                </p>
                <div className="flex space-x-2 text-xs text-gray-500">
                  <span className="flex items-center">
                    <FaCalendarAlt className="mr-1" /> Por Período
                  </span>
                  <span className="flex items-center">
                    <FaFileAlt className="mr-1" /> Por Proveedor
                  </span>
                </div>
              </div>
              <div className="p-3 bg-green-100 text-green-600 rounded-full">
                <FaChartBar className="h-6 w-6" />
              </div>
            </div>
          </Card>
        </Link>

        {/* Tarjeta: Stock y Movimientos */}
        <Link to="/stock" className="block transition transform hover:scale-105">
          <Card
            title="Stock y Movimientos"
            icon={<FaChartBar />}
          >
            <div className="flex justify-between items-center">
              <div className="space-y-2">
                <p className="text-gray-600">
                  Análisis de rotación de inventario y movimientos de productos.
                </p>
                <div className="flex space-x-2 text-xs text-gray-500">
                  <span className="flex items-center">
                    <FaChartLine className="mr-1" /> Rotación
                  </span>
                  <span className="flex items-center">
                    <FaFileAlt className="mr-1" /> Movimientos
                  </span>
                </div>
              </div>
              <div className="p-3 bg-blue-100 text-blue-600 rounded-full">
                <FaChartBar className="h-6 w-6" />
              </div>
            </div>
          </Card>
        </Link>

        {/* Tarjeta: Personalizado */}
        <Link to="/reportes/ganancias" className="block transition transform hover:scale-105">
          <Card
            title="Reporte Personalizado"
            icon={<FaFileAlt />}
          >
            <div className="flex justify-between items-center">
              <div className="space-y-2">
                <p className="text-gray-600">
                  Crea reportes con parámetros y criterios personalizados.
                </p>
                <div className="flex space-x-2 text-xs text-gray-500">
                  <span className="flex items-center">
                    <FaFileAlt className="mr-1" /> Configurable
                  </span>
                  <span className="flex items-center">
                    <FaChartLine className="mr-1" /> Exportable
                  </span>
                </div>
              </div>
              <div className="p-3 bg-red-100 text-red-600 rounded-full">
                <FaFileAlt className="h-6 w-6" />
              </div>
            </div>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default Reportes;
